import { config } from '@/lib/config';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DetailPane } from './components/DetailPane';
// Field-picker builder — hidden for now; message search is the shipped control.
// To restore: uncomment this, the `useTopicFields` import + `fields` below, and
// the `builderSlot` prop on MessageListPane.
// import { FilterBuilder } from './components/FilterBuilder';
import { MapPane } from './components/MapPane';
import { MessageListPane } from './components/MessageListPane';
import { PaneResizer } from './components/PaneResizer';
import { PublishPanel } from './components/PublishPanel';
import { TopicTreePane } from './components/TopicTreePane';
import { useDataStreamFeed } from './hooks/useDataStreamFeed';
import type { ZoomTarget } from './hooks/useDsMap';
import { useFilteredMessages } from './hooks/useFilteredMessages';
import { useMessageRate } from './hooks/useMessageRate';
import { usePaneResize } from './hooks/usePaneResize';
import { usePublishMessage } from './hooks/usePublishMessage';
// import { useTopicFields } from './hooks/useTopicFields';
import { useTopicMessages } from './hooks/useTopicMessages';
import { useTopicObjects } from './hooks/useTopicObjects';
import { useTopicTree } from './hooks/useTopicTree';
import { classifyTopic } from './utils/classifyTopic';
import { extractMapObjects } from './utils/extractMapObjects';
import { topicMatchesFilter } from './utils/filterDefs';
import { formatBytes } from './utils/formatBytes';
import type { FieldFilter } from './utils/payloadQuery';

export function DataStreamView() {
  useDataStreamFeed();

  const { tree, leafCount, liveCount, searching } = useTopicTree();
  const { messages, selected, selectedMsgId } = useTopicMessages();
  const {
    visible,
    total: bufferTotal,
    active: queryActive,
    filters: fieldFilters,
    terms,
    key: filterKey,
    accept,
  } = useFilteredMessages(messages);

  const selectedTopic = useDataStreamStore((s) => s.selectedTopic);
  const selectTopic = useDataStreamStore((s) => s.selectTopic);
  const selectMsg = useDataStreamStore((s) => s.selectMsg);
  const detailTab = useDataStreamStore((s) => s.detailTab);
  const setTab = useDataStreamStore((s) => s.setTab);
  const search = useDataStreamStore((s) => s.search);
  const setSearch = useDataStreamStore((s) => s.setSearch);
  const msgSearch = useDataStreamStore((s) => s.msgSearch);
  const setMsgSearch = useDataStreamStore((s) => s.setMsgSearch);
  const paused = useDataStreamStore((s) => s.paused);
  const autoPaused = useDataStreamStore((s) => s.autoPaused);
  const widths = useDataStreamStore((s) => s.paneWidths);
  const topics = useDataStreamStore((s) => s.topics);
  const filter = useDataStreamStore((s) => s.filter);
  const addFieldFilter = useDataStreamStore((s) => s.addFieldFilter);
  const removeFieldFilter = useDataStreamStore((s) => s.removeFieldFilter);
  const clearFieldFilters = useDataStreamStore((s) => s.clearFieldFilters);
  // Drones currently reporting telemetry — lets the tree explain an empty
  // per-drone filter (events/alerts/oi) as "waiting for a drone".
  const activeDroneCount = useDronesStore((s) => Object.keys(s.byId).length);
  // Fullscreen (topbar "Map" toggle): expand the DS map pane to fill the body
  // and hide the tree/messages/detail panes. The map cell stays mounted (keyed)
  // so its markers persist and only the canvas resizes.
  const fullscreen = useUiStore((s) => s.fullscreenMap);

  const { enabled: publishEnabled, state: pubState, publish } = usePublishMessage();
  const resizeTree = usePaneResize('tree');
  const resizeMsgs = usePaneResize('messages');
  const resizeDetail = usePaneResize('detail');

  // Keep the selection consistent with the active filter chip. On first load
  // (nothing selected) or whenever the selected topic no longer matches the
  // filter, re-point to the highest-traffic *matching* topic — or clear the
  // selection if nothing matches. This keeps the message list + map (both
  // keyed off selectedTopic) in sync with the filtered tree instead of
  // stranding a now-hidden topic on screen. While a topic search is active the
  // selection is confined to the surviving leaves, so the pane never shows a
  // topic the tree has hidden.
  const visibleLeaves = useMemo(() => {
    const out = new Set<string>();
    const walk = (n: (typeof tree)[number]) => {
      if (n.leaf) out.add(n.path);
      for (const c of n.children) walk(c);
    };
    for (const n of tree) walk(n);
    return out;
  }, [tree]);

  useEffect(() => {
    const matching = [...topics.values()].filter(
      (t) => topicMatchesFilter(t.topic, filter) && (!searching || visibleLeaves.has(t.topic)),
    );
    if (selectedTopic && matching.some((t) => t.topic === selectedTopic)) return;
    const top = matching.sort((a, b) => b.count - a.count)[0];
    const target = top ? top.topic : null;
    if (target !== selectedTopic) selectTopic(target);
  }, [selectedTopic, topics, filter, selectTopic, searching, visibleLeaves]);

  const badge = selectedTopic ? classifyTopic(selectedTopic) : 'system';
  // The map shows the same set the message list does — a filter that narrows
  // the rows narrows the plotted objects too.
  const objects = useTopicObjects(selectedTopic, { accept, filterKey });
  // The topic's own traffic is the schema: it drives the field picker.
  // const fields = useTopicFields(messages);

  const replaceFilter = useCallback(
    (oldId: string, next: FieldFilter) => {
      removeFieldFilter(oldId);
      addFieldFilter(next);
    },
    [removeFieldFilter, addFieldFilter],
  );

  // Zoom control. Two triggers feed the single target consumed by useDsMap.
  // A monotonic key forces useDsMap to re-zoom even if the coords repeat.
  const zoomKeyRef = useRef(0);
  const [zoomTarget, setZoomTarget] = useState<ZoomTarget | null>(null);

  // Bumped on every topic click so re-selecting the *same* topic re-fits.
  const [fitNonce, setFitNonce] = useState(0);
  const handleSelectTopic = useCallback(
    (topic: string | null) => {
      selectTopic(topic);
      setFitNonce((n) => n + 1);
    },
    [selectTopic],
  );

  // (1) Click-to-zoom: zoom to a message only when one is explicitly pinned.
  // Gating on selectedMsgId (not `selected`, which falls back to the newest
  // message) keeps the map from chasing every incoming message on a topic.
  useEffect(() => {
    if (!selectedMsgId || !selected || !selectedTopic) return;
    const objs = extractMapObjects(selected.payload, badge, selectedTopic);
    if (objs.length === 0) return;
    zoomKeyRef.current += 1;
    setZoomTarget({
      bounds: objs.map((o) => [o.lng, o.lat]),
      key: zoomKeyRef.current,
      mode: 'focus',
    });
  }, [selected, selectedMsgId, selectedTopic, badge]);

  // (2) Topic fit-all: selecting a topic (with no message pinned) fits the map
  // so every live object on that topic is visible — no default message pin and
  // no close zoom. Fits once per selection (keyed on topic + click nonce) then
  // holds; a pinned message owns the zoom.
  //
  // The fit is deferred a tick and reads objects via a ref, because on a topic
  // switch useTopicObjects resets to [] and re-folds the buffer *after* this
  // render — reading the render-time `objects` would fit to the previous
  // topic's stale set (or just the first object), leaving real objects
  // off-screen. By the time the timeout fires the new topic's buffer is folded.
  const objectsRef = useRef(objects);
  objectsRef.current = objects;
  const fittedKeyRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `objects` is read via objectsRef, but is listed so the effect re-runs (and reschedules the fit) once the new topic's objects arrive.
  useEffect(() => {
    if (selectedMsgId) return;
    if (!selectedTopic) {
      fittedKeyRef.current = null;
      return;
    }
    const key = `${selectedTopic}|${fitNonce}`;
    if (fittedKeyRef.current === key) return;
    const t = window.setTimeout(() => {
      const objs = objectsRef.current;
      if (objs.length === 0) return; // nothing yet — a later objects update reschedules
      fittedKeyRef.current = key;
      zoomKeyRef.current += 1;
      setZoomTarget({
        bounds: objs.map((o) => [o.lng, o.lat]),
        key: zoomKeyRef.current,
        mode: 'fit',
      });
    }, 200);
    return () => window.clearTimeout(t);
  }, [selectedTopic, selectedMsgId, objects, fitNonce]);

  // Per-topic stats for the message-list header — scoped to the selected topic
  // (not the global firehose) so each topic's volume and cadence are visible.
  const topicStat = selectedTopic ? topics.get(selectedTopic) : undefined;
  const topicCount = topicStat?.count ?? 0;
  const topicQos = topicStat?.lastQos ?? 0;
  const topicRate = useMessageRate(messages);
  // Average payload size across the topic's buffered messages — a steadier
  // per-topic characteristic than any single message's size.
  const topicSize = messages.length
    ? formatBytes(Math.round(messages.reduce((sum, m) => sum + m.bytes, 0) / messages.length))
    : '—';
  const defaultPubTopic = selectedTopic ?? `${config.mqtt.topicPrefix}/`;

  return (
    <div className="flex h-full w-full overflow-hidden bg-(--color-bg)">
      {!fullscreen && (
        <>
          <div style={{ width: widths.tree }} className="shrink-0 border-r border-(--color-border)">
            <TopicTreePane
              tree={tree}
              leafCount={leafCount}
              liveCount={liveCount}
              search={search}
              onSearch={setSearch}
              selectedPath={selectedTopic}
              onSelect={handleSelectTopic}
              filter={filter}
              activeDroneCount={activeDroneCount}
              searching={searching}
            />
          </div>
          <PaneResizer onMouseDown={resizeTree} />

          <div
            style={{ width: widths.messages }}
            className="shrink-0 border-r border-(--color-border)"
          >
            <MessageListPane
              topic={selectedTopic}
              badge={badge}
              messages={visible}
              selectedId={selectedMsgId}
              onSelect={selectMsg}
              paused={paused}
              total={topicCount}
              rate={topicRate}
              size={topicSize}
              qos={topicQos}
              filters={fieldFilters}
              terms={terms}
              bufferTotal={bufferTotal}
              searchActive={queryActive}
              onRemoveFilter={removeFieldFilter}
              onReplaceFilter={replaceFilter}
              onClearFilters={clearFieldFilters}
              search={msgSearch}
              onSearch={setMsgSearch}
              autoPaused={autoPaused}
              // builderSlot={<FilterBuilder fields={fields} onAdd={addFieldFilter} />}
            />
          </div>
          <PaneResizer onMouseDown={resizeMsgs} />

          <div
            style={{ width: widths.detail }}
            className="shrink-0 border-r border-(--color-border)"
          >
            <DetailPane
              selected={selected}
              messages={messages}
              tab={detailTab}
              onTab={setTab}
              onFilter={addFieldFilter}
              publishSlot={
                publishEnabled ? (
                  <PublishPanel
                    defaultTopic={defaultPubTopic}
                    state={pubState}
                    onPublish={publish}
                  />
                ) : null
              }
            />
          </div>
          <PaneResizer onMouseDown={resizeDetail} />
        </>
      )}

      {/* Keyed + always mounted so the map instance + markers survive the
          fullscreen toggle; only the canvas resizes (ResizeObserver). */}
      <div key="ds-map" className="min-w-[200px] flex-1">
        <MapPane objects={objects} zoomTarget={zoomTarget} />
      </div>
    </div>
  );
}
